import React, { useState } from "react";
import { StatusBar } from "expo-status-bar";
import {
  Platform,
  ScrollView,
  StyleSheet,
  useColorScheme,
  TouchableOpacity,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { TextInput, Pressable } from "react-native";
import { Text, View } from "../components/Themed";
import { useLinkTo } from "@react-navigation/native";
import { useQuery, useMutation, gql } from "@apollo/client";
import Config from "../constants/Config";
import PublicFooter from "../components/PublicFooter";

const INVITE_DETAILS = gql`
  query getInviteDetails($token: String!) {
    invite(token: $token) {
      id
      by
      created
      expires
      type
      destination
    }
  }
`;

const CREATE_CREDENTIAL = gql`
  mutation createCredential($token: String!, $password: String!) {
    createCredential(token: $token, password: $password)
  }
`;

function RequirementRow({ met, label }) {
  return (
    <View style={styles.requirementRow}>
      <MaterialIcons
        name={met ? "check-circle" : "cancel"}
        size={18}
        color={met ? "#34c759" : "#ccc"}
        style={{ marginRight: 8 }}
      />
      <Text style={[styles.requirementText, met && styles.requirementMet]}>
        {label}
      </Text>
    </View>
  );
}

export default function PublicInviteScreen(props) {
  const linkTo = useLinkTo();
  const token = props.route.params && props.route.params.token;
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const { loading, error, data } = useQuery(INVITE_DETAILS, {
    variables: { token: token },
    nextFetchPolicy: "cache-only",
  });

  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [creating, setCreating] = useState(false);

  const passRegex = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{10,}$/;
  const symbolRegex = /[!@#$%^&*]+/;
  const lowerRegex = /[a-z]+/;
  const upperRegex = /[A-Z]+/;
  const digitRegex = /\d+/;

  const lengthOk = password.length >= 10;
  const symbolOk = symbolRegex.test(password);
  const upperOk = upperRegex.test(password);
  const lowerOk = lowerRegex.test(password);
  const digitOk = digitRegex.test(password);
  const passwordsMatch = password && repeatPassword && password === repeatPassword;
  const allValid = passRegex.test(password) && passwordsMatch;

  const [createCredential] = useMutation(CREATE_CREDENTIAL);

  const accentColor = Config.companyColor || "#007AFF";

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <MaterialIcons name="hourglass-empty" size={48} color={accentColor} />
        <Text style={[styles.statusText, isDark && styles.textLight]}>
          Loading your invitation...
        </Text>
      </View>
    );
  }

  if (!data || !data.invite) {
    return (
      <View style={styles.centerContainer}>
        <MaterialIcons name="error-outline" size={64} color="#ff3b30" />
        <Text style={[styles.errorTitle, isDark && styles.textLight]}>
          Invalid Invitation
        </Text>
        <Text style={[styles.errorSubtitle, isDark && styles.textMuted]}>
          This invitation link is not valid. Please ask staff for a new one.
        </Text>
        <Pressable
          style={[styles.linkButton, { borderColor: accentColor }]}
          onPress={() => props.navigation.replace("Menu")}
        >
          <Text style={[styles.linkButtonText, { color: accentColor }]}>
            Go to Main Menu
          </Text>
        </Pressable>
      </View>
    );
  }

  const expires = new Date(parseInt(data.invite.expires));
  if (expires < new Date()) {
    return (
      <View style={styles.centerContainer}>
        <MaterialIcons name="schedule" size={64} color="#ff9500" />
        <Text style={[styles.errorTitle, isDark && styles.textLight]}>
          Invitation Expired
        </Text>
        <Text style={[styles.errorSubtitle, isDark && styles.textMuted]}>
          This invitation has expired. Please ask staff for a new one.
        </Text>
        <Pressable
          style={[styles.linkButton, { borderColor: accentColor }]}
          onPress={() => props.navigation.replace("Menu")}
        >
          <Text style={[styles.linkButtonText, { color: accentColor }]}>
            Go to Main Menu
          </Text>
        </Pressable>
      </View>
    );
  }

  async function handleCreate() {
    setCreating(true);
    setErrorMessage("");
    try {
      const result = await createCredential({
        variables: { token, password },
      });
      if (result?.data?.createCredential) {
        props.navigation.replace("OperatorRoot", {
          screen: "OperatorSettingsRoot",
        });
      } else {
        setErrorMessage("Something went wrong creating your credentials.");
      }
    } catch (e) {
      setErrorMessage(e.message || "An unexpected error occurred.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <ScrollView
      style={[styles.scrollView, isDark && styles.scrollViewDark]}
      contentContainerStyle={styles.scrollContent}
    >
      <View style={styles.card}>
        <MaterialIcons
          name="person-add"
          size={48}
          color={accentColor}
          style={{ marginBottom: 16 }}
        />

        <Text style={styles.welcomeText}>{Config.invitationMessage}</Text>

        <Text style={styles.emailLabel}>Your account</Text>
        <View style={styles.emailBadge}>
          <MaterialIcons name="email" size={18} color="#666" style={{ marginRight: 8 }} />
          <Text style={styles.emailText}>{data.invite.destination}</Text>
        </View>

        <Text style={styles.sectionLabel}>Create your password</Text>

        <TextInput
          secureTextEntry
          onChangeText={setPassword}
          placeholder="Password"
          placeholderTextColor="#999"
          value={password}
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="new-password"
          textContentType="newPassword"
          style={[styles.input, isDark && styles.inputDark]}
        />

        <TextInput
          secureTextEntry
          onChangeText={setRepeatPassword}
          placeholder="Confirm password"
          placeholderTextColor="#999"
          value={repeatPassword}
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="new-password"
          textContentType="newPassword"
          style={[styles.input, isDark && styles.inputDark]}
        />

        <View style={styles.requirementsCard}>
          <Text style={styles.requirementsTitle}>Password requirements</Text>
          <RequirementRow met={lengthOk} label="At least 10 characters" />
          <RequirementRow met={upperOk} label="One uppercase letter" />
          <RequirementRow met={lowerOk} label="One lowercase letter" />
          <RequirementRow met={digitOk} label="One number" />
          <RequirementRow met={symbolOk} label="One special character (!@#$%^&*)" />
          {password && repeatPassword && !passwordsMatch && (
            <View style={styles.requirementRow}>
              <MaterialIcons name="cancel" size={18} color="#ff3b30" style={{ marginRight: 8 }} />
              <Text style={[styles.requirementText, { color: "#ff3b30" }]}>
                Passwords do not match
              </Text>
            </View>
          )}
        </View>

        <Pressable
          style={[
            styles.createButton,
            { backgroundColor: accentColor },
            !allValid && styles.createButtonDisabled,
          ]}
          onPress={handleCreate}
          disabled={!allValid || creating}
        >
          {creating ? (
            <Text style={styles.createButtonText}>Creating...</Text>
          ) : (
            <Text style={styles.createButtonText}>Create Account</Text>
          )}
        </Pressable>

        {errorMessage ? (
          <Text style={styles.errorText}>{errorMessage}</Text>
        ) : null}
      </View>
      <PublicFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: "#f2f2f7",
  },
  scrollViewDark: {
    backgroundColor: "#000",
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    paddingVertical: 40,
  },
  centerContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 20,
    padding: 32,
    width: "100%",
    maxWidth: 420,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 8,
  },
  welcomeText: {
    fontSize: 20,
    fontWeight: "700",
    color: "#333",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 28,
  },
  emailLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    alignSelf: "flex-start",
    marginBottom: 6,
  },
  emailBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f0f0f5",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    width: "100%",
    marginBottom: 24,
  },
  emailText: {
    fontSize: 15,
    color: "#333",
    fontWeight: "500",
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    alignSelf: "flex-start",
    marginBottom: 10,
  },
  input: {
    width: "100%",
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#f5f5f5",
    fontSize: 16,
    marginBottom: 12,
    color: "#000",
  },
  inputDark: {
    backgroundColor: "#e8e8e8",
  },
  requirementsCard: {
    width: "100%",
    backgroundColor: "#fafafa",
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    marginTop: 4,
  },
  requirementsTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  requirementRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    backgroundColor: "transparent",
  },
  requirementText: {
    fontSize: 14,
    color: "#999",
  },
  requirementMet: {
    color: "#333",
  },
  createButton: {
    width: "100%",
    padding: 16,
    borderRadius: 12,
    alignItems: "center",
    marginTop: 4,
  },
  createButtonDisabled: {
    opacity: 0.4,
  },
  createButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "600",
  },
  errorText: {
    color: "#ff3b30",
    marginTop: 16,
    textAlign: "center",
    fontSize: 14,
  },
  statusText: {
    fontSize: 16,
    color: "#666",
    marginTop: 16,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#333",
    marginTop: 16,
    marginBottom: 8,
  },
  errorSubtitle: {
    fontSize: 15,
    color: "#666",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 22,
  },
  textLight: {
    color: "#fff",
  },
  textMuted: {
    color: "#aaa",
  },
  linkButton: {
    borderWidth: 1.5,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  linkButtonText: {
    fontSize: 15,
    fontWeight: "600",
  },
});
